import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, Search, User, LayoutDashboard, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/auth";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import moeLogoUrl from "@/assets/brand/moe-logo.webp";

interface HeaderProps {
  onAiSearch?: (query: string) => void;
}

/**
 * Header - Main navigation header for public pages
 * Features: Logo, navigation links, search, language switcher, mobile menu with animations
 */
function Header({ onAiSearch }: HeaderProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { isAuthenticated, signOut, viewAs } = useAuth();

  // In a view-as tab this control must not read as "sign out": it ends the
  // impersonation and returns the tab to the Super Admin, and nobody is signed
  // out of anything.
  const signOutLabel = viewAs
    ? "Exit view-as"
    : null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", labelKey: "navigation.home" },
    { to: "/services", labelKey: "navigation.services" },
    { to: "/track-application", labelKey: "navigation.trackApplication" },
    { to: "/help", labelKey: "navigation.helpCenter" },
    { to: "/guide", labelKey: "navigation.guide" },
    { to: "/about", labelKey: "navigation.about" },
  ];

  const submitSearch = (query: string) => {
    const trimmed = query.trim();

    if (onAiSearch) {
      onAiSearch(trimmed);
    } else if (trimmed) {
      navigate(`/services?search=${encodeURIComponent(trimmed)}`);
    } else {
      navigate(`/services`);
    }

    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    setIsMobileMenuOpen(false);
    await signOut();
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-4 xs:px-4 sm:px-6 sm:pt-5 lg:px-8">
      <div
        className={`mx-auto flex max-w-[1600px] items-center justify-between gap-2 transition-all duration-300 xs:gap-4 ${
          isScrolled ? "[&>div]:shadow-ambient-lg" : "[&>div]:shadow-ambient"
        }`}
      >
        {/* ── Left floating pill: logo + nav ── */}
        <div className="flex min-w-0 flex-1 items-center gap-3 rounded-pill bg-white/95 py-2.5 pl-2.5 pr-2 backdrop-blur-xl transition-all duration-300 dark:bg-[rgb(var(--color-surface-container-lowest))]/90 xs:pl-3 xs:pr-3 lg:gap-4 lg:pl-4 lg:pr-6 wide:gap-8 wide:pr-8">
          {/* Logo */}
          <Link to="/" className="group flex min-w-0 items-center gap-1.5 xs:gap-2 sm:gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-0.5 transition-transform group-hover:scale-105 dark:bg-[rgb(var(--color-surface-container-high))]">
              <img
                src={moeLogoUrl}
                alt="MOE Logo"
                className="h-full w-full object-contain dark:brightness-110"
              />
            </div>
            <span className="truncate font-display text-[14px] font-bold tracking-tight text-on-surface transition-colors group-hover:text-secondary-500 xs:text-[15px] sm:text-[17px]">
              {t("header.ministryName")}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden shrink-0 flex-1 items-center justify-center gap-0.5 lg:flex wide:gap-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `relative whitespace-nowrap px-2 py-2 text-[14px] font-medium transition-colors wide:px-3 ${
                    isActive
                      ? "text-secondary-500"
                      : "text-on-surface hover:text-secondary-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {t(link.labelKey)}
                    {isActive && (
                      <span className="absolute inset-x-2 -bottom-0.5 h-[2px] rounded-full bg-secondary-500 wide:inset-x-3" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Inline icon actions (desktop only, tucked into left pill) */}
          <div className="hidden shrink-0 items-center gap-1 lg:flex">
            {/* Language Switcher */}
            <LanguageSwitcher variant="dropdown" />

            <ThemeToggle />
          </div>
        </div>

        {/* ── Right floating pill: staff login + avatar ── */}
        {isAuthenticated ? (
          <div className="hidden shrink-0 items-center gap-3 lg:flex">
            <Link
              to="/admin"
              aria-label={t("navigation.adminPortal", {
                defaultValue: "Admin Portal",
              })}
              className="group flex items-center gap-3 rounded-pill bg-white/95 py-2.5 pl-2.5 pr-2.5 backdrop-blur-xl transition-all duration-300 hover:bg-white dark:bg-[rgb(var(--color-surface-container-lowest))]/90 wide:pl-6"
            >
              <span className="hidden whitespace-nowrap text-[14px] font-semibold text-on-surface transition-colors group-hover:text-secondary-500 wide:inline">
                {t("navigation.adminPortal", {
                  defaultValue: "Admin Portal",
                })}
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-pill bg-surface-subtle text-on-surface-variant transition-colors group-hover:bg-secondary-500 group-hover:text-white dark:bg-[rgb(var(--color-surface-container))]">
                <LayoutDashboard size={16} strokeWidth={2.2} />
              </div>
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              aria-label={signOutLabel ??
                t("navigation.signOut", { defaultValue: "Sign Out" })}
              className="group flex items-center gap-3 rounded-pill border border-outline-variant/20 bg-white/95 py-2.5 pl-2.5 pr-2.5 backdrop-blur-xl transition-all duration-300 hover:bg-white dark:bg-[rgb(var(--color-surface-container-lowest))]/90 wide:pl-6"
            >
              <span className="hidden whitespace-nowrap text-[14px] font-semibold text-on-surface transition-colors group-hover:text-secondary-500 wide:inline">
                {signOutLabel ??
                  t("navigation.signOut", { defaultValue: "Sign Out" })}
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-pill bg-surface-subtle text-on-surface-variant transition-colors group-hover:bg-secondary-500 group-hover:text-white dark:bg-[rgb(var(--color-surface-container))]">
                <LogOut size={16} strokeWidth={2.2} />
              </div>
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            aria-label={t("navigation.staffLogin")}
            className="group hidden shrink-0 items-center gap-3 rounded-pill bg-white/95 py-2.5 pl-2.5 pr-2.5 backdrop-blur-xl transition-all duration-300 hover:bg-white dark:bg-[rgb(var(--color-surface-container-lowest))]/90 lg:flex wide:pl-6"
          >
            <span className="hidden whitespace-nowrap text-[14px] font-semibold text-on-surface transition-colors group-hover:text-secondary-500 wide:inline">
              {t("navigation.staffLogin")}
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-pill bg-surface-subtle text-on-surface-variant transition-colors group-hover:bg-secondary-500 group-hover:text-white dark:bg-[rgb(var(--color-surface-container))]">
              <User size={16} strokeWidth={2.2} />
            </div>
          </Link>
        )}

        {/* Mobile menu (still uses pill container) */}
        {/* ── Mobile pill: theme + sheet trigger ── */}
        <div className="flex shrink-0 items-center gap-0.5 rounded-pill bg-white/95 px-1.5 py-2 backdrop-blur-xl dark:bg-[rgb(var(--color-surface-container-lowest))]/90 xs:gap-1 xs:px-2 lg:hidden">
          <ThemeToggle />
          <button
            onClick={() => submitSearch("")}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-on-surface transition-colors hover:bg-surface-subtle hover:text-secondary-500 dark:hover:bg-[rgb(var(--color-surface-container))]"
            aria-label={t("search.askAi")}
          >
            <Search size={18} />
          </button>
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-pill text-on-surface transition-colors hover:bg-surface-subtle hover:text-secondary-500 dark:hover:bg-[rgb(var(--color-surface-container))]"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="top"
              className="top-0 rounded-b-[32px] border-0 p-0 pt-20 sm:max-w-full"
            >
              <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-6 pb-6">
                {navLinks.map((link) => (
                  <SheetClose asChild key={link.to}>
                    <NavLink
                      to={link.to}
                      end={link.to === "/"}
                      className={({ isActive }) =>
                        `rounded-xl px-4 py-3 text-[15px] font-semibold transition-colors ${
                          isActive
                            ? "text-secondary-500"
                            : "text-on-surface hover:bg-surface-subtle hover:text-secondary-500 dark:hover:bg-[rgb(var(--color-surface-container))]"
                        }`
                      }
                    >
                      {t(link.labelKey)}
                    </NavLink>
                  </SheetClose>
                ))}

                {/* Language switcher */}
                <div className="mt-4 px-4 pt-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
                    {t("language.selectLanguage", {
                      defaultValue: "Select Language",
                    })}
                  </p>
                  <LanguageSwitcher variant="inline" />
                </div>

                {/* Staff actions */}
                <div className="mt-6 px-2">
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-3">
                      <SheetClose asChild>
                        <Button
                          asChild
                          variant="cta"
                          size="lg"
                          className="w-full"
                        >
                          <Link to="/admin">
                            <LayoutDashboard className="mr-1" />
                            {t("navigation.adminPortal", {
                              defaultValue: "Admin Portal",
                            })}
                          </Link>
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          type="button"
                          variant="subtle"
                          size="lg"
                          className="w-full"
                          onClick={handleSignOut}
                        >
                          <LogOut className="mr-1" />
                          {signOutLabel ??
                            t("navigation.signOut", {
                              defaultValue: "Sign Out",
                            })}
                        </Button>
                      </SheetClose>
                    </div>
                  ) : (
                    <SheetClose asChild>
                      <Button
                        asChild
                        variant="cta"
                        size="lg"
                        className="w-full"
                      >
                        <Link to="/login">{t("navigation.staffLogin")}</Link>
                      </Button>
                    </SheetClose>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Header;
